import { useEffect, useState } from "react";
import * as api from "../api/client";

/** Returns `true` when the current user is an admin AND the
 *  server-side `:admin_mode` cheat is on. Used to gate the Skip
 *  (ADMIN) button on the challenge fight modal and the Reset button
 *  on the challenge panel. The /api/admin/cheats endpoint is
 *  admin-only, so we only call it after confirming `is_admin` to
 *  avoid noisy 403s for regular users.
 *
 *  Returns `null` while the answer is still loading — callers should
 *  render nothing or a placeholder during that window. */
export function useAdminMode(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const me = await api.getMe();
        if (cancelled) return;

        if (!me.user.is_admin) {
          setEnabled(false);
          return;
        }

        try {
          const cheats = await api.getAdminCheats();
          if (!cancelled) setEnabled(!!cheats.admin_mode);
        } catch {
          // Cheats endpoint hiccup — fail closed so the admin button
          // doesn't appear without a confirmed toggle. Login wall
          // failures already showed earlier.
          if (!cancelled) setEnabled(false);
        }
      } catch {
        // /me failure — treat as not-admin.
        if (!cancelled) setEnabled(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return enabled;
}
